import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { LayoutModule } from '@angular/cdk/layout';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { AgGridModule } from 'ag-grid-angular';
import { ChartsModule } from 'ng2-charts';
import { AvatarModule } from 'ngx-avatar';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ColorPickerModule } from 'ngx-color-picker';
import { IconPickerModule } from 'ngx-icon-picker';
import { PERFECT_SCROLLBAR_CONFIG, PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { MaterialModule } from '../../app.material';
import { HeaderTitleService } from '../core';
import { SharedModule } from '../shared/shared.module';
import {
	AlertDialogComponent,
	DiemDialogComponent,
	IconDialogComponent,
	NoteDialogComponent,
	TransactionDialogComponent,
	TripDialogComponent,
	SetAdminDialogComponent,
	TimeSheetHistoryDialogComponent
} from './dialogs';
import {
	AddEmployeeComponent,
	AdminDashboardComponent,
	AircraftModelsComponent,
	AircraftModelsDetailsComponent,
	AircraftModelsCardComponent,
	CategoryCardComponent,
	ChangesDisplayComponent,
	DashboardComponent,
	DetailsCardComponent,
	DumpCardComponent,
	FooterComponent,
	JobTitlesComponent,
	JobTitlesDetailsComponent,
	JobTitlesCardComponent,
	NotesCardComponent,
	PageNotFoundComponent,
	PayCategoriesComponent,
	PayCategoriesDetailsComponent,
	PayScalesHistoryCardComponent,
	PayScaleCardComponent,
	PayScalesOverviewComponent,
	PayScalesComponent,
	PayScalesDetailsCardComponent,
	PayScalesDetailsComponent,
	PayScalesSettingsCardComponent,
	PayScalesSettingsComponent,
	PayScalesTripsCardComponent,
	PayScalesYearsOfServiceCardComponent,
	PilotpayComponent,
	PayScalesPilotsCardComponent,
	SettingsCardComponent,
	TimeSheetsComponent,
	TimeSheetCardComponent,
	OfflineComponent,
	AddEmployeeDialogComponent
} from './index';
import { PayCategoriesService } from './pay-categories/pay-categories.service';
import { AircraftModelsService } from './aircraft-models/aircraft-models.service';
import { JobTitlesService } from './job-titles/job-titles.service';
import { CurrencyComponent } from './pay-scales/cards/years-of-service/currency.component';
import { NumericEditorComponent } from './pay-scales/cards/years-of-service/numeric.component';
import { TwoDigitDecimalEditorComponent } from './pay-scales/cards/years-of-service/two-digit-decimal.component';
import { PayScalesService } from './pay-scales/pay-scales.service';
import { PilotpayRoutingModule } from './pilotpay-routing.module';
import { PilotPayVersionService } from './pilotpay-version.service';
import { CalendarModule } from 'primeng/calendar';
import { TimeSheetsStoreService } from './time-sheets/time-sheets.store';
import { ToolbarComponent } from './toolbar/toolbar.component';
import { ReconciliationTotalsComponent } from './time-sheets/cards/totals-cards/reconciliation-totals.component';
import { InstructionTotalsComponent } from './time-sheets/cards/totals-cards/instruction-totals.component';
import { NotificationsModule } from '../notifications/notifications.module';
import { HoursSummaryTotalsComponent } from './time-sheets/cards/totals-cards/hours-summary-totals.component';
import { PilotsTableComponent } from './admin-dashboard/pilots-table/pilots-table.component';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { PilotsService } from '../shared/services/pilots.service';
import {
	MatMomentDateModule,
	MomentDateAdapter,
	MAT_MOMENT_DATE_ADAPTER_OPTIONS
} from '@angular/material-moment-adapter';
import { OverrideEmployeeDialogComponent } from './dialogs/override-employee-dialog/override-employee-dialog.component';
import { OverrideEmployeesComponent } from './override-employees/override-employees.component';
import { EditableHeaderComponent } from './pay-scales/cards/years-of-service/editable-header.component';
import { AddEmployeeService } from './add-employee/add-employee.service';
import { ErrorsComponent } from '../shared/errors/errors.component';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
	suppressScrollX: true
};

const avatarColors: string[] = ['#FFB6C1', '#2c3e50', '#95a5a6', '#f39c12', '#1abc9c'];

/**
 * PilotpayModule
 */
@NgModule({
	imports: [
		CommonModule,
		BsDropdownModule.forRoot(),
		PilotpayRoutingModule,
		MaterialModule,
		MatMomentDateModule,
		FlexLayoutModule,
		LayoutModule,
		ChartsModule,
		FormsModule,
		SharedModule,
		ReactiveFormsModule,
		ColorPickerModule,
		IconPickerModule,
		CalendarModule,
		NgxMaterialTimepickerModule,
		AvatarModule.forRoot({
			colors: avatarColors
		}),
		AgGridModule.withComponents([
			PayScalesYearsOfServiceCardComponent,
			PayScalesPilotsCardComponent,
			CurrencyComponent,
			PayScalesPilotsCardComponent,
			NumericEditorComponent,
			TwoDigitDecimalEditorComponent,
			EditableHeaderComponent
		]),
		NotificationsModule
	],
	declarations: [
		ErrorsComponent,
		AddEmployeeComponent,
		AddEmployeeDialogComponent,
		FooterComponent,
		PageNotFoundComponent,
		PilotpayComponent,
		DashboardComponent,
		AdminDashboardComponent,
		ChangesDisplayComponent,
		DumpCardComponent,
		CategoryCardComponent,
		PayScalesComponent,
		PayScalesDetailsComponent,
		AircraftModelsComponent,
		AircraftModelsDetailsComponent,
		AircraftModelsCardComponent,
		JobTitlesComponent,
		JobTitlesDetailsComponent,
		JobTitlesCardComponent,
		TimeSheetsComponent,
		DiemDialogComponent,
		PayCategoriesComponent,
		TransactionDialogComponent,
		TripDialogComponent,
		SetAdminDialogComponent,
		PayScalesOverviewComponent,
		NoteDialogComponent,
		AlertDialogComponent,
		IconDialogComponent,
		DetailsCardComponent,
		SettingsCardComponent,
		NotesCardComponent,
		PayCategoriesDetailsComponent,
		PayScalesSettingsCardComponent,
		PayScalesDetailsCardComponent,
		PayScalesTripsCardComponent,
		PayScalesPilotsCardComponent,
		PayScaleCardComponent,
		PayScalesYearsOfServiceCardComponent,
		ReconciliationTotalsComponent,
		InstructionTotalsComponent,
		HoursSummaryTotalsComponent,
		CurrencyComponent,
		NumericEditorComponent,
		TwoDigitDecimalEditorComponent,
		PayScalesSettingsComponent,
		PayScalesHistoryCardComponent,
		TimeSheetCardComponent,
		ToolbarComponent,
		PilotsTableComponent,
		OfflineComponent,
		EditableHeaderComponent,
		OverrideEmployeesComponent,
		OverrideEmployeeDialogComponent,
		TimeSheetHistoryDialogComponent
	],
	providers: [
		{
			provide: PERFECT_SCROLLBAR_CONFIG,
			useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG
		},
		CurrencyPipe,
		DatePipe,
		PilotPayVersionService,
		PayCategoriesService,
		AircraftModelsService,
		JobTitlesService,
		AddEmployeeService,
		HeaderTitleService,
		PayScalesService,
		PilotsService,
		TimeSheetsStoreService,
		{ provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { hasBackdrop: true } },
		{ provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
		{ provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true, strict: true } }
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
	exports: [ErrorsComponent],
	entryComponents: [
		AddEmployeeDialogComponent,
		DiemDialogComponent,
		TransactionDialogComponent,
		TripDialogComponent,
		SetAdminDialogComponent,
		NoteDialogComponent,
		AlertDialogComponent,
		IconDialogComponent,
		OverrideEmployeeDialogComponent,
		TimeSheetHistoryDialogComponent,
		ChangesDisplayComponent
	]
})

/**
 * PilotpayModule
 */
export class PilotpayModule {}
